/**
 * POLISH NAMES CLEANUP PAGE (Admin Tool)
 *
 * Bulk-cleanup for polish `name` variants within a brand. Load Mooncat's
 * catalog and see every distinct polish name with item + collector
 * counts, then rename typos ("404 Soul Not Found" → "404: Soul Not
 * Found") or fold multiple variants into a canonical name via the shared
 * `renamePolish()` cascade helper — same cascade the single-polish
 * `MergePolishes` page uses, but batched here for admin convenience.
 *
 * Cascade coverage (see `services/polishRename.ts` docstring):
 *   1. All 7 identity fields on user items — brand, name, slug,
 *      brandLower, nameLower, brandSlug, nameSlug.
 *   2. polishConfirms migrated to the new slug.
 *   3. Creations' itemsUsed[] snapshots rewritten and itemsUsedRefs[]
 *      recomputed (dedupe included).
 *   4. polishCatalog rebuild happens automatically via the
 *      `onUserPolishWrite` trigger.
 *
 * Brand-first workflow: at launch scale there are ~34k polishes across
 * ~1,600 brands. Loading them all in one scroll would be unusable, and
 * the natural mental model is "clean up Mooncat's names, then Essie's,
 * then …". So the admin picks a brand first, then a per-brand list of
 * ~500 rows loads. Fast, focused, and cheap.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getDocs,
  doc,
  getDoc,
  collection,
  query,
  where,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, Pencil, Check, X } from 'lucide-react';
import { slugify } from '../utils/polishSlug';
import { renamePolish } from '../services/polishRename';

interface PolishRow {
  /** Doc id in polishCatalog — `${brandSlug}__${nameSlug}`. Stable
   *  identity for the row across renders; also the "from slug" if the
   *  admin renames it. */
  slug: string;
  brand: string;
  name: string;
  collectorCount: number;
  photoCount: number;
}

export default function PolishNamesCleanup() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const [brandInput, setBrandInput] = useState('');
  const [loadedBrand, setLoadedBrand] = useState<string | null>(null);
  const [rows, setRows] = useState<PolishRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Inline-rename state — mirrors CollectionsCleanup. Only one row is
  // editable at a time so the UI stays predictable.
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const [canonicalName, setCanonicalName] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const say = (m: string) => {
    setLog((prev) => [...prev, m]);
    console.log('[PolishNamesCleanup]', m);
  };

  const loadBrand = async () => {
    const brand = brandInput.trim();
    if (!brand) {
      say('Enter a brand name first.');
      return;
    }
    const brandSlug = slugify(brand);
    if (!brandSlug) {
      say(`"${brand}" slugifies to empty — check the value.`);
      return;
    }
    setLoading(true);
    setLog([]);
    setSelected(new Set());
    setEditingSlug(null);
    say(`Loading polishes for brand "${brand}" (brandSlug: ${brandSlug})…`);
    try {
      // polishCatalog is already keyed per canonical polish and carries
      // collectorCount + photoCount from the trigger — cheapest source.
      const snap = await getDocs(
        query(collection(db, 'polishCatalog'), where('brandSlug', '==', brandSlug))
      );
      const items: PolishRow[] = snap.docs
        .map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            slug: d.id,
            brand: typeof data.brand === 'string' ? data.brand : brand,
            name: typeof data.name === 'string' ? data.name : '',
            collectorCount:
              typeof data.collectorCount === 'number' ? data.collectorCount : 0,
            photoCount:
              typeof data.photoCount === 'number' ? data.photoCount : 0,
          };
        })
        .filter((r) => r.name)
        .sort(
          (a, b) =>
            b.collectorCount - a.collectorCount || a.name.localeCompare(b.name)
        );
      setRows(items);
      setLoadedBrand(brand);
      say(
        `Loaded ${items.length} distinct polish name${items.length === 1 ? '' : 's'} under this brand.`
      );
      if (items.length === 0) {
        say(
          'No results. Possible causes: brand not in polishCatalog yet (needs the trigger to have fired for at least one item), or the brand string doesn\'t match. Try the exact casing you see on the brand page.'
        );
      }
    } catch (err) {
      say(`Load failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(f));
  }, [rows, filter]);

  const totalSelected = selected.size;
  const totalSelectedItems = useMemo(() => {
    let n = 0;
    for (const r of rows) if (selected.has(r.slug)) n += r.collectorCount;
    return n;
  }, [rows, selected]);

  const toggleSelect = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of filteredRows) next.add(r.slug);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  /** Inline rename: rewrites ONE polish name in place, keeping the same
   *  brand. Skips the dry-run gate — single-row renames are small stakes.
   *  Full cascade via `renamePolish()`. */
  const runRenameOne = async (row: PolishRow, toName: string) => {
    const trimmed = toName.trim();
    if (!trimmed) {
      say('New name cannot be empty.');
      return;
    }
    if (trimmed === row.name) {
      setEditingSlug(null);
      return;
    }
    setIsRunning(true);
    try {
      const summary = await renamePolish({
        fromBrand: row.brand,
        fromName: row.name,
        toBrand: row.brand,
        toName: trimmed,
        onLog: say,
      });
      say(
        `Rename OK: items ${summary.itemsUpdated}, confirms ${summary.confirmsMigrated}, creations ${summary.creationsUpdated}.`
      );
      setEditingSlug(null);
      // Refresh so the row's slug/name reflect the new state.
      await loadBrand();
    } catch (err) {
      say(`Rename failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Bulk merge: fold every selected variant into `canonicalName`, all
   *  under the same brand. Runs `renamePolish()` sequentially per source
   *  — parallelism would race the shared polishCatalog trigger. */
  const runBulkMerge = async () => {
    const canonical = canonicalName.trim();
    if (!canonical) {
      say('Enter a canonical polish name first.');
      return;
    }
    if (totalSelected === 0) {
      say('Nothing selected.');
      return;
    }
    if (!loadedBrand) {
      say('Load a brand first.');
      return;
    }
    setIsRunning(true);
    say(
      `${dryRun ? '[DRY-RUN] ' : ''}Merging ${totalSelected} variant${totalSelected === 1 ? '' : 's'} → "${canonical}" under brand "${loadedBrand}"…`
    );
    let ranSuccessfully = 0;
    let totalItems = 0;
    let totalConfirms = 0;
    let totalCreations = 0;
    try {
      const selectedRows = rows.filter((r) => selected.has(r.slug));
      for (const row of selectedRows) {
        if (row.name === canonical) {
          say(`  Skipping "${row.name}" — already canonical.`);
          continue;
        }
        try {
          const summary = await renamePolish({
            fromBrand: row.brand,
            fromName: row.name,
            toBrand: loadedBrand,
            toName: canonical,
            dryRun,
            onLog: say,
          });
          totalItems += summary.itemsUpdated;
          totalConfirms += summary.confirmsMigrated;
          totalCreations += summary.creationsUpdated;
          ranSuccessfully++;
        } catch (err) {
          say(`  "${row.name}" failed: ${(err as Error).message}`);
        }
      }
      say(
        `${dryRun ? '(dry-run) ' : ''}Bulk merge complete. Sources processed: ${ranSuccessfully}. Total items ${dryRun ? 'would-update' : 'updated'}: ${totalItems}. Confirms ${dryRun ? 'would-migrate' : 'migrated'}: ${totalConfirms}. Creations ${dryRun ? 'would-update' : 'updated'}: ${totalCreations}.`
      );
      if (!dryRun) {
        await loadBrand();
      }
    } catch (err) {
      say(`Bulk merge failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Checking access…
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in to use this admin tool.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2">Polish Names Cleanup</h1>
      <p className="text-glass-secondary text-sm mb-3">
        Consolidate polish <code>name</code> variants within a brand.
        Renames cascade through <code>items</code>, <code>polishConfirms</code>,
        and creations automatically. Pick a brand, load its polish list, then
        rename typos in place or fold variants into one canonical name.
      </p>
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-3 mb-6 text-glass-secondary text-xs space-y-1">
        <div>
          <span className="inline-flex items-center gap-1 mr-1 text-glass">
            <Pencil className="w-3 h-3" /> Rename
          </span>{' '}
          — fix a single typo/casing on one row. Runs immediately, cascades
          across items + confirms + creations.
        </div>
        <div>
          <span className="text-glass">Bulk merge</span> — check multiple
          variants + type the canonical name below. Dry-run supported so you
          can preview counts first. Runs sequentially — the shared catalog
          trigger doesn't tolerate parallel writes on the same slug.
        </div>
      </div>

      {/* Brand picker */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
        <label className="block text-xs text-glass-muted mb-1">Brand</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={brandInput}
            onChange={(e) => setBrandInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadBrand();
            }}
            placeholder="e.g. Mooncat, Essie, Loaded Lacquer"
            disabled={loading || isRunning}
            className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          <button
            onClick={loadBrand}
            disabled={loading || isRunning || !brandInput.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : loadedBrand ? 'Reload' : 'Load polishes'}
          </button>
        </div>
        {loadedBrand && rows.length > 0 && (
          <div className="text-glass-secondary text-xs mt-3">
            {rows.length} distinct polish{rows.length === 1 ? '' : 'es'} · {totalSelected} selected ({totalSelectedItems} collectors)
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <>
          {/* Filter + bulk-select controls */}
          <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter polish name — e.g. '404', 'Willow', 'Reflect'"
              className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass mb-3"
            />
            <div className="flex gap-2 text-sm">
              <button
                onClick={selectAllFiltered}
                disabled={filteredRows.length === 0}
                className="px-3 py-1.5 bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50"
              >
                Select all filtered ({filteredRows.length})
              </button>
              <button
                onClick={clearSelection}
                disabled={selected.size === 0}
                className="px-3 py-1.5 bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50"
              >
                Clear selection
              </button>
            </div>
          </div>

          {/* List */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg mb-4 max-h-96 overflow-y-auto">
            {filteredRows.length === 0 ? (
              <div className="p-4 text-glass-muted text-sm">
                No polishes match this filter.
              </div>
            ) : (
              <ul>
                {filteredRows.map((r) => {
                  const isEditing = editingSlug === r.slug;
                  return (
                    <li
                      key={r.slug}
                      className="flex items-start gap-3 px-4 py-2 border-b border-slate-800 hover:bg-slate-800/50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(r.slug)}
                        onChange={() => toggleSelect(r.slug)}
                        className="rounded mt-1"
                        disabled={isEditing}
                        title="Check to include in bulk merge below"
                      />
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  runRenameOne(r, editingValue);
                                if (e.key === 'Escape') setEditingSlug(null);
                              }}
                              autoFocus
                              className="flex-1 bg-slate-900 border border-purple-500 rounded px-2 py-1 text-glass text-sm"
                            />
                            <button
                              onClick={() => runRenameOne(r, editingValue)}
                              disabled={isRunning}
                              title="Save rename (cascades immediately)"
                              className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingSlug(null)}
                              disabled={isRunning}
                              title="Cancel"
                              className="p-1 rounded bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div
                            className="text-glass text-sm truncate"
                            title={`${r.name} · slug: ${r.slug}`}
                          >
                            {r.name}
                          </div>
                        )}
                        <div className="text-glass-muted text-[10px] mt-0.5">
                          slug: <code>{r.slug}</code>
                        </div>
                      </div>
                      <div className="text-glass-muted text-xs whitespace-nowrap mt-1 mr-2">
                        {r.collectorCount} collector{r.collectorCount === 1 ? '' : 's'} · {r.photoCount} photo{r.photoCount === 1 ? '' : 's'}
                      </div>
                      {!isEditing && (
                        <button
                          onClick={() => {
                            setEditingSlug(r.slug);
                            setEditingValue(r.name);
                          }}
                          disabled={isRunning}
                          title="Rename (fix typo / casing / formatting)"
                          className="p-1.5 rounded text-glass-muted hover:bg-slate-700 hover:text-glass disabled:opacity-30"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Bulk merge action */}
          <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
            <h2 className="text-glass font-medium mb-2">Bulk merge → canonical</h2>
            <p className="text-glass-muted text-xs mb-3">
              Fold every checked variant into the canonical name below. Runs
              one rename per source in sequence (safe for the catalog
              trigger). Use dry-run first if you're unsure.
            </p>
            <label className="block text-xs text-glass-muted mb-1">
              Canonical polish name
            </label>
            <input
              type="text"
              value={canonicalName}
              onChange={(e) => setCanonicalName(e.target.value)}
              placeholder='e.g. "404: Soul Not Found"'
              disabled={isRunning}
              className="w-full mb-3 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
            />
            <label className="flex items-center gap-2 mb-3 text-glass text-sm">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry-run (preview counts without writing)
            </label>
            <button
              onClick={runBulkMerge}
              disabled={
                isRunning || totalSelected === 0 || !canonicalName.trim()
              }
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              {isRunning
                ? 'Working…'
                : dryRun
                ? 'Preview merge'
                : `Merge ${totalSelected} → canonical`}
            </button>
          </div>
        </>
      )}

      {/* Log */}
      {log.length > 0 && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
